import React, { useState, useRef, useEffect } from 'react';
import { Notification } from '@/api';
import type { User, NotificationData } from '@/types/layout.types';
import {
	JOIN_MATCHMAKING_ROOM,
	LEAVE_MATCHMAKING_ROOM,
	ON_MATCHMAKING,
	JOIN_NOTIFICATIONS_ROOM,
	LEAVE_NOTIFICATIONS_ROOM,
	ON_NOTIFICATIONS,
	NotificationChannel,
	NotificationType,
} from '@sportspredict/shared';
import { notifyOnce as notifyOnceUtil } from '@/utils';

interface UseNotificationsReturn {
	liveNotifications: NotificationData[];
	setLiveNotifications: React.Dispatch<React.SetStateAction<NotificationData[]>>;
	notificationsReady: boolean;
	setNotificationsReady: React.Dispatch<React.SetStateAction<boolean>>;
	notifyOnce: (key: string, email: string | undefined, title: string, message: string) => void;
	handleClose: (id: string) => void;
}

export function useNotifications(
	user: User | null | undefined,
	socket: any
): UseNotificationsReturn {
	const [liveNotifications, setLiveNotifications] = useState<NotificationData[]>([]);
	const [notificationsReady, setNotificationsReady] = useState(false);
	const notifiedRef = useRef(new Set<string>());

	const notifyOnce = (key: string, email: string | undefined, title: string, message: string) => {
		notifyOnceUtil(notifiedRef, key, email, title, message);
	};

	const toastListener = async (data: NotificationData) => {
		if (!data) return;
		if (data.channel !== NotificationChannel.AppToast) return;
		if (data.type === NotificationType.ProbabilityAchievement) return;
		if (data.type === NotificationType.NewsfeedArticle) return;
		if (
			Array.isArray(data.targetUsers) &&
			(!user?.email || !data.targetUsers.includes(user.email))
		) {
			return;
		}

		const n = await Notification.get(data.id);
		setLiveNotifications((prev) => [...prev, n]);
	};

	const matchmakingListener = (data: { matchName: string; h2hMatchId: string }) => {
		const newNotification: NotificationData = {
			id: data.h2hMatchId,
			title: 'Opponent is found',
			message: data.matchName || 'Match is starting soon',
			toast_type: 'info',
		};

		setLiveNotifications((prev) => [...prev, newNotification]);
	};

	useEffect(() => {
		if (!socket || !user?.id) return;

		socket.emit(JOIN_NOTIFICATIONS_ROOM);
		socket.on(ON_NOTIFICATIONS, toastListener);

		socket.emit(JOIN_MATCHMAKING_ROOM, { userId: user.id });
		socket.on(ON_MATCHMAKING, matchmakingListener);

		return () => {
			socket.emit(LEAVE_NOTIFICATIONS_ROOM);
			socket.off(ON_NOTIFICATIONS, toastListener);

			socket.emit(LEAVE_MATCHMAKING_ROOM, { userId: user.id });
			socket.off(ON_MATCHMAKING, matchmakingListener);
		};
	}, [socket, user?.id]);

	const handleClose = (id: string) => {
		setLiveNotifications((prev) => prev.filter((n) => n.id !== id));
	};

	return {
		liveNotifications,
		setLiveNotifications,
		notificationsReady,
		setNotificationsReady,
		notifyOnce,
		handleClose,
	};
}
